import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import swal from "sweetalert";
import {
  CheckPermission,
  customOption,
  customSingleValue,
  LoadingSpinner,
} from "../../../constants/const";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { ApiFinance } from "../../config/finance/ApiFinance";
import { GetLanguageString } from "../../helper/Components";
import { GetCustomStyles } from "../../helper/Method";
import { ApiFinance as ApiFinanceLocal } from "../ApiFinance";

const CustomSelect = ({
  label,
  name,
  options,
  value,
  onChange,
  loading,
  error,
  placeholder,
}) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Select
      components={{ Option: customOption, SingleValue: customSingleValue }}
      options={options}
      name={name}
      value={value}
      className="basic-select no-drag"
      classNamePrefix="select"
      isLoading={loading}
      isDisabled={options.length === 0}
      onChange={onChange}
      placeholder={placeholder}
      {...(error && { invalid: true })}
    />
    {error && <h4 className="text-error">{error}</h4>}
  </FormGroup>
);

const CustomInput = ({ label, name, value, onChange, error }) => (
  <FormGroup>
    <Label className="col-form-label">
      <GetLanguageString props={label} />
    </Label>
    <Input
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      className="no-drag"
    />
    {error && <h4 className="text-error no-drag">{error}</h4>}
  </FormGroup>
);

const DEFAULT_FORM = {
  selectedFinanceCategory: null,
  selectedAccount: null,
  selectedDimension: null,
  date: new Date(),
  amount: "",
  memo: "",
  quantity: "",
  price: "",
  serialNumber: "",
};

const matchesOption = (option, values) => {
  const optionValues = [
    option?.value,
    option?.id,
    option?.label,
    option?.label1,
    option?.name,
    option?.title,
    option?.code,
  ]
    .map((item) => String(item || "").trim().toLowerCase())
    .filter(Boolean);

  return values.some((value) =>
    optionValues.includes(String(value || "").trim().toLowerCase()),
  );
};

const findOption = (options, values) =>
  options.find((option) => matchesOption(option, values)) || null;

const parseExpenseMemo = (memo) => {
  const parsedMemo = String(memo || "").trim();
  const match = parsedMemo.match(/^-?(\d+)\s*x\s*(\d+)\s*\((.*)\)$/);

  if (!match) {
    return {
      memo: parsedMemo,
      quantity: "",
      price: "",
    };
  }

  return {
    quantity: String(match[1] || "").replace(/^0+/, "") || "0",
    price: String(match[2] || "").replace(/^0+/, "") || "0",
    memo: match[3] || "",
  };
};

const EditExpenseFromCashInHand = ({
  setModal,
  transactionData,
  unitId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const unitTypeName = encryptStorage.getItem("unitTypeName");

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState({
    source: false,
    accounts: false,
    dimensions: false,
    submit: false,
  });
  const [financeAccounts, setFinanceAccounts] = useState([]);
  const [financeDimensions, setFinanceDimensions] = useState([]);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});

  const toggle = () => setModal(false);

  const handleDrag = (e, ui) => {
    setPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const padTo4Digits = (value) => {
    if (!value) return "0000";
    return String(value).padStart(4, "0");
  };

  const showQuantityPrice =
    formData.selectedFinanceCategory?.label1 === "asset" ||
    formData.selectedFinanceCategory?.label1 === "maktaba" ||
    formData.selectedFinanceCategory?.label1 === "misc";

  const isMaktabaOrLoanMarkaz = formData.selectedFinanceCategory?.label1?.includes(
    "markaz",
  );

  const isLoanIndividual =
    formData.selectedFinanceCategory?.label1 === "return_loan_individual";

  const isBusy = Object.values(loading).some(Boolean);

  useEffect(() => {
    if (!transactionData) return;

    const parsedMemo = parseExpenseMemo(transactionData.memo);

    setLoading((prev) => ({ ...prev, source: true }));
    ApiFinance()
      .GetFinanceAccountCategoriesListByType("expense")
      .then((res) => {
        const categories = res.data || [];

        const selectedFinanceCategory =
          findOption(categories, [
            transactionData?.categoryId,
            transactionData?.categoryID,
            transactionData?.title,
            transactionData?.label1,
          ]) ||
          categories[0] ||
          null;

        setFormData({
          selectedFinanceCategory,
          selectedAccount: null,
          selectedDimension: null,
          date: transactionData.date ? new Date(transactionData.date) : new Date(),
          amount: transactionData.amount || "",
          memo: parsedMemo.memo,
          quantity: parsedMemo.quantity,
          price: parsedMemo.price,
          serialNumber: transactionData.serialNumber || "",
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load expense categories.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, source: false }));
      });
  }, [transactionData, unitTypeName]);

  useEffect(() => {
    const categoryId = formData.selectedFinanceCategory?.value;
    if (!categoryId) return;

    setLoading((prev) => ({ ...prev, accounts: true, dimensions: true }));
    setFinanceAccounts([]);
    setFinanceDimensions([]);
    setFormData((prev) => ({
      ...prev,
      selectedAccount: null,
      selectedDimension: null,
    }));

    ApiFinance()
      .GetFinanceCategoryAccountsById(categoryId)
      .then((res) => {
        const accounts = res.data || [];
        setFinanceAccounts(accounts);

        setFormData((prev) => ({
          ...prev,
          selectedAccount:
            findOption(accounts, [
              transactionData?.codeId,
              transactionData?.accountName,
              transactionData?.bankName,
            ]) ||
            accounts[0] ||
            null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load expense heads.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, accounts: false }));
      });

    ApiFinance()
      .GetFinanceCategoryDimensionsById(categoryId)
      .then((res) => {
        const dimensions = res.data || [];
        setFinanceDimensions(dimensions);

        setFormData((prev) => ({
          ...prev,
          selectedDimension:
            findOption(dimensions, [
              transactionData?.dimension2Id,
              transactionData?.subHeadName,
              transactionData?.bankName,
            ]) ||
            dimensions[0] ||
            null,
        }));
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to load expense sub-heads.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, dimensions: false }));
      });
  }, [formData.selectedFinanceCategory, transactionData]);

  const validate = () => {
    const nextErrors = {
      amount: formData.amount ? "" : <GetLanguageString props="funds_add_fund_amount_paid_error" />,
    };

    setErrors(nextErrors);
    return Object.values(nextErrors).every((value) => value === "");
  };

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!validate()) return;

    const payload = {
      financeAccount: formData.selectedAccount ?? null,
      dimension2: formData.selectedDimension ?? null,
      category: formData.selectedFinanceCategory ?? null,
      date: formData.date,
      amount: formData.amount,
      serialNumber: formData.serialNumber,
      memo: showQuantityPrice
        ? `${padTo4Digits(formData.quantity)} x ${padTo4Digits(formData.price)} (${formData.memo})`
        : formData.memo,
      unitId: unitId || transactionData?.unitId || loggedInUnitId,
      transactionNo : transactionData?.transactionNo,
    };

    setLoading((prev) => ({ ...prev, submit: true }));
    ApiFinanceLocal()
      .EditExpense(payload)
      .then((res) => {
        const data = res.data || {};

        if (Number(data.statusCode) !== 200) {
          swal({
            title: "Error",
            text: data.message || "Failed to edit Expense",
            icon: "error",
            buttons: "OK",
          });
          return;
        }

        swal({
          title: "Success",
          text: t(data.message) || t("funds_expense_updated_successfully"),
          icon: "success",
          buttons: "OK",
        }).then(() => {
          setModal(false);
          if (typeof onSuccess === "function") {
            onSuccess();
          }
        });
      })
      .catch((err) => {
        console.error(err);
        swal({
          title: "Error",
          text: err.response?.data || "Unable to update expense.",
          icon: "error",
          buttons: "OK",
        });
      })
      .finally(() => {
        setLoading((prev) => ({ ...prev, submit: false }));
      });
  };

  return (
    <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
      <Modal isOpen className="inmodal" size="xl" autoFocus={false}>
        <ModalHeader className="modal-title" tag="h4">
          <GetLanguageString props="finance_expense_cash_in_hand" />
        </ModalHeader>

        {loading.source || loading.submit ? (
          <ModalBody className="text-center">
            <LoadingSpinner />
          </ModalBody>
        ) : (
          <Form onSubmit={handleSubmit} noValidate>
            <ModalBody>
              <Row>
                {isMaktabaOrLoanMarkaz ? null : isLoanIndividual ? (
                  <Col md="3">
                    <CustomInput
                      name="serialNumber"
                      label="notifications_rafiq_serial_number"
                      value={formData.serialNumber}
                      onChange={(e) =>
                        handleChange("serialNumber", e.target.value)
                      }
                      error={errors.serialNumber}
                    />
                  </Col>
                ) : (
                  <>
                    <Col md="3">
                      <CustomSelect
                        name="selectedAccount"
                        label="funds_expense_head"
                        options={financeAccounts}
                        value={formData.selectedAccount}
                        onChange={(value) => handleChange("selectedAccount", value)}
                        error={errors.selectedAccount}
                        loading={loading.accounts}
                        placeholder={
                          loading.accounts
                            ? t("finance_expense_heads_loading")
                            : financeAccounts.length > 0
                              ? t("finance_expense_heads_select")
                              : t("finance_expense_heads_empty")
                        }
                      />
                    </Col>
                    <Col md="3">
                      <CustomSelect
                        name="selectedDimension"
                        label="funds_expense_sub_head"
                        options={financeDimensions}
                        value={formData.selectedDimension}
                        onChange={(value) =>
                          handleChange("selectedDimension", value)
                        }
                        error={errors.selectedDimension}
                        loading={loading.dimensions}
                        placeholder={
                          loading.dimensions
                            ? t("finance_expense_sub_heads_loading")
                            : financeDimensions.length > 0
                              ? t("finance_expense_sub_heads_select")
                              : t("finance_expense_sub_heads_empty")
                        }
                      />
                    </Col>
                  </>
                )}

                <Col md="3">
                  <FormGroup>
                    <Label className="col-form-label">
                      <GetLanguageString props="funds_date" />
                    </Label>
                    <DatePicker
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="dd/mm/yyyy"
                      className="form-control no-drag"
                      selected={formData.date}
                      maxDate={new Date()}
                      onChange={(date) =>
                        handleChange("date", date ?? new Date())
                      }
                      dateFormat="dd/MM/yyyy"
                    />
                  </FormGroup>
                </Col>

                <Col md="3">
                  <CustomInput
                    name="memo"
                    label="transactions_memo"
                    value={formData.memo}
                    onChange={(e) => handleChange("memo", e.target.value)}
                    error={errors.memo}
                  />
                </Col>

                <Col md="3">
                  <CustomInput
                    name="amount"
                    label="funds_add_fund_amount"
                    value={formData.amount}
                    onChange={(e) => handleChange("amount", e.target.value)}
                    error={errors.amount}
                  />
                </Col>

                {showQuantityPrice && (
                  <Col md="3" className="d-flex">
                    <div style={{ flex: "0 0 50%", paddingRight: "8px" }}>
                      <CustomInput
                        name="quantity"
                        label="finance_other_income_quantity"
                        value={formData.quantity}
                        onChange={(e) =>
                          handleChange("quantity", e.target.value)
                        }
                        error={errors.quantity}
                      />
                    </div>

                    <div style={{ flex: "0 0 50%", paddingLeft: "8px" }}>
                      <CustomInput
                        name="price"
                        label="finance_other_income_price"
                        value={formData.price}
                        onChange={(e) => handleChange("price", e.target.value)}
                        error={errors.price}
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </ModalBody>

            <ModalFooter>
              {CheckPermission("CanAddFunds") ? (
                <>
                  <Button
                    color="white"
                    className={GetCustomStyles().btn_style}
                    onClick={toggle}
                  >
                    <GetLanguageString props="common_cancel" />
                  </Button>
                  <Button
                    color="primary"
                    className={GetCustomStyles().btn_style}
                    disabled={isBusy}
                    type="submit"
                  >
                    <GetLanguageString props="common_save_changes" />
                  </Button>
                </>
              ) : null}
            </ModalFooter>
          </Form>
        )}
      </Modal>
    </Draggable>
  );
};

export default EditExpenseFromCashInHand;
